/**
 * v6.1.3 Phase G4 Part A — WebGL availability probe.
 *
 * Returns `true` when the current browser/environment can create a
 * WebGL context, `false` otherwise.  Used by 3D components (Globe3D,
 * any Three.js hero) to fall back to a static visual instead of
 * letting Three.js throw at `new WebGLRenderer(...)` and bubble into
 * the top-level ErrorBoundary.
 *
 * Result is memoised — the answer cannot change at runtime within the
 * same document (a tab either has GPU access or it doesn't), and the
 * probe itself involves creating a throwaway <canvas> which we want
 * to avoid doing on every render.
 *
 * Caller convention:
 *
 *   import { isWebGLAvailable } from '../lib/isWebGLAvailable';
 *   if (!isWebGLAvailable()) return <StaticFallback />;
 *   // ... safe to call new THREE.WebGLRenderer(...)
 *
 * Headless environments (Playwright/Puppeteer without `--enable-webgl`
 * and `--use-gl=swiftshader`, JSDOM, CI without GPU) all return false.
 * Real browsers on real devices effectively always return true unless
 * the user has explicitly disabled hardware acceleration.
 */

let _CACHE = null;

export function isWebGLAvailable() {
  if (_CACHE !== null) return _CACHE;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // SSR / jest jsdom without canvas-mock — treat as unavailable so
    // the static fallback renders.  The next call from the client side
    // re-probes once the canvas API is real.
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');
    _CACHE = !!gl;
    // Best-effort cleanup of the probe context — important so we
    // don't burn one of the ~16 contexts most browsers cap at.
    if (gl && typeof gl.getExtension === 'function') {
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose && typeof lose.loseContext === 'function') lose.loseContext();
    }
    return _CACHE;
  } catch (_err) {
    _CACHE = false;
    return false;
  }
}

/**
 * Test/Storybook helper — reset the cache so a fresh probe runs.
 * Not used in production code paths.
 */
export function _resetWebGLAvailabilityCache() {
  _CACHE = null;
}

export default isWebGLAvailable;
